var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useEffect, useRef, useState, useCallback } from 'react';
import cx from 'classnames';
import Clickable from '@move-ui/clickable';
import { isEmpty, omit } from 'lodash';
import Text, { TextSize } from '@move-ui/text';
import { LoadingIndicator } from '@move-ui/indicator';
import { CancelIcon } from '@move-ui/icons-asset-catalog';
import { mergeRefs } from '@move-web-essentials-utils/ref';
import useClickOutsideObserver from '@move-web-essentials-hooks/use-click-outside-observer';
import { TextInput } from '@move-ui/input';
import { useValidationOnKeyUp } from './hooks/useValidationOnKeyUp';
import styles from './Autosuggest.module.css';
import Suggestion from './Suggestion';
import { stopEvent } from './utils/stopEvent';
var getIcon = function (_a) {
    var value = _a.value, iconRight = _a.iconRight, isResolving = _a.isResolving, onClickIcon = _a.onClickIcon, onClearInput = _a.onClearInput;
    if (isResolving) {
        return React.createElement(LoadingIndicator, { className: styles.icon });
    }
    if (!isEmpty(value)) {
        // use tabindex -1 to allow text input blur
        return (React.createElement(Clickable, { "data-testid": "ResetInputIcon", tabIndex: -1, onClick: onClearInput, className: styles.clickable },
            React.createElement(CancelIcon, null)));
    }
    if (iconRight) {
        return (React.createElement(Clickable, { "data-testid": "CustomInputIcon", tabIndex: -1, onClick: onClickIcon, className: styles.clickable }, iconRight));
    }
    return undefined;
};
export var Autosuggest = function (props) {
    var _a, _b;
    var hint = props.hint, message = props.message, value = props.value, isValid = props.isValid, className = props.className, iconRight = props.iconRight, suggestions = props.suggestions, isResolving = props.isResolving, menuClassName = props.menuClassName, optionClassName = props.optionClassName, containerClassName = props.containerClassName, _c = props.initialFocusIndex, initialFocusIndex = _c === void 0 ? -1 : _c, _d = props.withItemDivider, withItemDivider = _d === void 0 ? false : _d, inputRef = props.inputRef, onKeyUp = props.onKeyUp, onSelect = props.onSelect, onClickIcon = props.onClickIcon, onClearInput = props.onClearInput, onFocus = props.onFocus, onBlur = props.onBlur, renderFooter = props.renderFooter, renderSuggestion = props.renderSuggestion;
    var ref = useRef(null);
    var containerRef = useRef(null);
    var _e = useState(false), isOpen = _e[0], setIsOpen = _e[1];
    var _f = useState(false), selected = _f[0], setSelected = _f[1];
    var _g = useState(value), selectedValue = _g[0], setSelectedValue = _g[1];
    var _h = useState(initialFocusIndex), focusedIndex = _h[0], setFocusedIndex = _h[1];
    var _j = useValidationOnKeyUp(onKeyUp), inputValidState = _j[0], handleExtendedOnKeyUp = _j[1];
    useEffect(function () {
        import('focus-visible').catch(function () { return null; });
    }, []);
    useEffect(function () {
        setSelectedValue(value);
    }, [value]);
    useEffect(function () {
        if (ref.current !== document.activeElement ||
            isResolving ||
            !value ||
            message) {
            setIsOpen(false);
            setFocusedIndex(initialFocusIndex);
            setSelected(false);
            return;
        }
        if (ref.current === document.activeElement &&
            !!(suggestions === null || suggestions === void 0 ? void 0 : suggestions.length) &&
            value &&
            !selected) {
            setIsOpen(true);
        }
    }, [
        value,
        selected,
        suggestions,
        isOpen,
        isResolving,
        initialFocusIndex,
        message
    ]);
    useClickOutsideObserver(containerRef, function () { return isOpen && setIsOpen(false); });
    var handleSelect = useCallback(function (suggestion) {
        var _a;
        onSelect(suggestion);
        setSelectedValue(suggestion === null || suggestion === void 0 ? void 0 : suggestion.value);
        setIsOpen(false);
        setSelected(true);
        (_a = ref === null || ref === void 0 ? void 0 : ref.current) === null || _a === void 0 ? void 0 : _a.blur();
    }, [setIsOpen, onSelect]);
    var focusNext = useCallback(function () {
        var _a;
        if (!suggestions) {
            return;
        }
        var newFocusIndex = focusedIndex === suggestions.length - 1 ? 0 : focusedIndex + 1;
        setFocusedIndex(newFocusIndex);
        setSelectedValue((_a = suggestions[newFocusIndex]) === null || _a === void 0 ? void 0 : _a.value);
    }, [focusedIndex, suggestions]);
    var focusPrevious = useCallback(function () {
        var _a;
        if (!suggestions) {
            return;
        }
        var newFocusIndex = focusedIndex <= 0 ? suggestions.length - 1 : focusedIndex - 1;
        setFocusedIndex(newFocusIndex);
        setSelectedValue((_a = suggestions[newFocusIndex]) === null || _a === void 0 ? void 0 : _a.value);
    }, [focusedIndex, suggestions]);
    var focusParent = useCallback(function () {
        var _a;
        focusNext();
        (_a = ref === null || ref === void 0 ? void 0 : ref.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, [focusNext]);
    var handleKeyDown = useCallback(function (event) {
        var _a, _b, _c;
        var key = event.key;
        if (!isOpen || message) {
            return;
        }
        if (key === 'ArrowDown') {
            focusNext();
            stopEvent(event);
            (_a = ref === null || ref === void 0 ? void 0 : ref.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
        if (key === 'ArrowUp') {
            focusPrevious();
            stopEvent(event);
            (_b = ref === null || ref === void 0 ? void 0 : ref.current) === null || _b === void 0 ? void 0 : _b.focus();
        }
        if (key === 'Enter' && focusedIndex !== -1) {
            handleSelect(suggestions === null || suggestions === void 0 ? void 0 : suggestions[focusedIndex]);
            stopEvent(event);
        }
        if (key === 'Escape') {
            setIsOpen(false);
            setFocusedIndex(initialFocusIndex);
            setSelected(false);
            stopEvent(event);
            // reset to the original value
            setSelectedValue(value);
            (_c = ref === null || ref === void 0 ? void 0 : ref.current) === null || _c === void 0 ? void 0 : _c.blur();
        }
    }, [
        value,
        isOpen,
        initialFocusIndex,
        focusNext,
        focusPrevious,
        focusedIndex,
        suggestions,
        handleSelect,
        message
    ]);
    var _k = useState(false), isFocused = _k[0], setIsFocused = _k[1];
    var handleFocus = function (event) {
        setIsFocused(true);
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(event);
    };
    var handleBlur = function (event) {
        setIsFocused(false);
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(event);
    };
    return (React.createElement("div", { className: styles.container, ref: containerRef },
        React.createElement(TextInput, __assign({ autoComplete: "off", inputRef: mergeRefs(ref, inputRef) }, omit(props, [
            'hint',
            'value',
            'className',
            'isResolving',
            'suggestions',
            'menuClassName',
            'withItemDivider',
            'optionClassName',
            'initialFocusIndex',
            'containerClassName',
            'onSelect',
            'inputRef',
            'onClickIcon',
            'onClearInput',
            'renderFooter',
            'renderSuggestion'
        ]), { value: selectedValue || value, iconRight: getIcon({
                value: value,
                iconRight: iconRight,
                isResolving: isResolving,
                onClickIcon: onClickIcon,
                onClearInput: onClearInput
            }), onKeyDown: handleKeyDown, onKeyUp: handleExtendedOnKeyUp, onFocus: handleFocus, onBlur: handleBlur, isValid: inputValidState && isValid, className: cx(className, (_a = {},
                _a[styles.open] = isOpen || (isFocused && message),
                _a)) })),
        isFocused && message && (React.createElement("div", { className: cx(styles.menuContainer, styles.message), "data-testid": "Autosuggest-Message" }, message)),
        isOpen && !!(suggestions === null || suggestions === void 0 ? void 0 : suggestions.length) && (React.createElement("div", { className: cx(styles.menuContainer, containerClassName, (_b = {},
                _b[styles.closed] = !isOpen,
                _b)), "data-testid": "Autosuggest-MenuContainer" },
            React.createElement("ul", { "data-testid": "Autosuggest-Menu", className: cx(styles.menu, menuClassName), "aria-hidden": !isOpen, role: "menu" }, suggestions.map(function (suggestion, index) {
                var _a;
                return (React.createElement(Suggestion, { onClick: function (elem) {
                        handleSelect(elem);
                        setSelectedValue(elem === null || elem === void 0 ? void 0 : elem.value);
                    }, key: suggestion.value, suggestion: suggestion, tabIndex: isOpen ? 0 : -1, onFocusParent: focusParent, className: cx(optionClassName, (_a = {},
                        _a[styles.optionMarginBottom] = index === suggestions.length - 1 && Boolean(!renderFooter),
                        _a)), isFocused: focusedIndex === index, withDivider: withItemDivider && index < suggestions.length - 1, renderSuggestion: function () { return renderSuggestion(suggestion); } }));
            })),
            renderFooter && (React.createElement("div", { className: styles.footerContainer }, renderFooter())))),
        hint && (React.createElement(Text, { size: TextSize.XXS, color: "neutral-200", className: styles.hint }, hint))));
};
//# sourceMappingURL=Autosuggest.js.map